import React, { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useParams } from 'react-router';
import { Spin, Card, Row, Col, Image, Button, App } from 'antd';
import { queries } from '../../../graphql/graphql';
import { PlanDetails } from '../../../components/Plans/PlanDetails';
import { MetaInfo } from '../../../components/Plans/MetaPlanInfo';
import { PlanOwnerInfo } from '../../../components/PlanOwner/PlanOwnerInfo';
import { AvailableArea } from '../../../components/Plans/AvailableArea';
import { EyeOutlined, DownloadOutlined } from '@ant-design/icons';
import axios from 'axios';
import { IPlan, IPlanPhoto } from '../../../types/plans';
import { getFileExtension } from '../../../utils/utils';

const PhotoGrid = ({ plan }: { plan: IPlan }) => {
  const { message } = App.useApp();
  const [loading, setLoading] = useState<number>(NaN);

  if (!plan.photos) return null;

  const handleOpenImage = (url: string) => {
    window.open(url, '_blank');
  };

  const handleDownload = async (url: string, index: number) => {
    try {
      setLoading(index);
      const response = await axios.post(
        '/api/download/image',
        { url },
        {
          responseType: 'blob',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      const contentType = response.headers['content-type'];
      const extension = getFileExtension(contentType);
      const blob = new Blob([response.data], { type: contentType });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${plan.title}-${index + 1}.${extension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Download error:', error);
      message.error(
        `Error downloading photo: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    } finally {
      setLoading(NaN);
    }
  };

  return (
    <Row gutter={[16, 16]}>
      {plan.photos.map((file: IPlanPhoto, index: number) => (
        <Col key={`file-${index}`} xs={24} sm={12} md={8}>
          <Card
            hoverable
            loading={loading == index}
            cover={
              loading !== index && (
                <Image
                  src={file.original}
                  alt={`${plan.title}-photo-${index + 1}`}
                  style={{ aspectRatio: 1 / 1, objectFit: 'cover' }}
                />
              )
            }
            actions={[
              <Button
                key="view"
                type="link"
                disabled={loading > 0}
                onClick={() => handleOpenImage(file.original)}
                icon={<EyeOutlined />}>
                View
              </Button>,
              <Button
                key="download"
                type="link"
                disabled={loading > 0}
                onClick={() => handleDownload(file.original, index)}
                icon={<DownloadOutlined />}>
                Download
              </Button>,
            ]}
            styles={{
              body: {
                display: loading !== index ? 'none' : 'block',
                aspectRatio: 1 / 1,
                objectFit: 'cover',
              },
            }}
          />
        </Col>
      ))}
    </Row>
  );
};

export const AdminPlanView = () => {
  const { id } = useParams();
  const {
    loading: planLoading,
    data: planData = {},
    error: planError,
    refetch,
  } = useQuery(queries.plans.PLAN, {
    variables: { id },
    fetchPolicy: 'network-only',
  });

  const { plan } = planData;

  const {
    loading: planOwnerLoading,
    data: planOwnerData = {},
    refetch: refetchPlanOwner,
  } = useQuery(queries.planOwners.PLAN_OWNER, {
    variables: { id: plan && plan.planOwnerId },
    skip: !plan || !plan.planOwnerId,
  });

  useEffect(() => {
    refetch();
    refetchPlanOwner();
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [refetch, refetchPlanOwner]);

  if (planError) return <div>Error loading plan: {planError.message}</div>;
  if (planLoading || !plan) return <Spin size="large" />;

  const { planOwner } = planOwnerData;

  return (
    <Card>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <PlanDetails plan={plan} />
        </Col>

        <Col span={24} lg={6}>
          <MetaInfo meta={plan.meta} />
        </Col>
        <Col span={24} lg={6}>
          {plan.planSVG && (
            <Card variant="borderless" size="small" title="Plan SVG">
              <Image
                src={`https://3d.homewip.com/svg/${plan.planId}/`}
                title="plan"
                aria-label="plan"
                style={{ width: '100%', height: 'auto', maxWidth: '100%' }}
              />
            </Card>
          )}
        </Col>
        <Col span={24} lg={6}>
          <Card variant="borderless" size="small" title="Plan Owner Information">
            <PlanOwnerInfo loading={planOwnerLoading} planOwner={planOwner} />
          </Card>
        </Col>
        <Col span={24} lg={6}>
          <Card variant="borderless" size="small" title="Available Areas">
            <AvailableArea available={plan.available || {}} />
          </Card>
        </Col>

        <Col span={24}>
          <PhotoGrid plan={plan} />
        </Col>
      </Row>
    </Card>
  );
};
